import { ComponentType, ReactElement } from "react";
import { logFrontendError } from "../services/errorReporter";

/**
 * Wraps a page-level component with a try/catch so render errors become structured log entries.
 * Why: guarantees every route surface reports failures into the centralized app.log sink.
 */
export const withPageGuard = <Props extends Record<string, unknown>>(
    Component: ComponentType<Props>,
    label: string,
    fallback?: ReactElement
): ComponentType<Props> => {
    const GuardedComponent = (props: Props) => {
        try {
            return <Component {...props} />;
        } catch (renderError) {
            void logFrontendError(`${label}.render`, `${label} failed to render`, renderError);
            return (
                fallback ?? (
                    <div className="page-error" role="alert">
                        We hit a snag while loading this page. Please refresh or contact support.
                    </div>
                )
            );
        }
    };
    GuardedComponent.displayName = `WithPageGuard(${Component.displayName ?? Component.name ?? label})`;
    return GuardedComponent;
};
